import * as React from "react";
import { Center, Gapped } from "react-ui";
import { ScrollContainer, Sticky } from "@skbkontur/react-ui";

import { ActionButtonContainer } from "../../ActionButton/ActionButtonContainer";
import { SendActionButtonContainer } from "../../ActionButton/SendActionButtonContainer";
import { FormSaveStatusContainer as FormSaveStatus } from "../../Panel/FormSaveStatus/FormSaveStatusContainer";
import { CheckingStatus } from "../CheckingStatus/CheckingStatus";
import { ErrorsListContainer as ErrorsList } from "../ErrorList/ErrorsListContainer";
import { OpenExtendedErrorLogFromPlace } from "../ErrorLogModal/ErrorLogModalLabel";
import { ErrorLogCounters } from "../ErrorLogModal/ErrorLogCounters/ErrorLogCounters";
import { PrintErrorLogContainer as PrintErrorLog } from "../PrintErrorLog/PrintErrorLogContainer";
import { PopdProtocolContainer as PopdProtocol } from "../ErrorList/ErrorItem/PopdProtocolContainer";
import { ErrorLog } from "../../../../Engine/Core/ErrorManager/Utils/Models";
import { ActionPanelButtonWidth } from "../../Panel/PanelTypes";
import { ErrorGroupHeaderSticky } from "../ErrorList/ErrorsGroup/ErrorGroupHeaderSticky";
import { checkSendDisabled, hasErrors } from "../../../../Engine/Core/ErrorManager/Utils/Utils";
import { ErrorLogModalLabelContainer as ErrorLogModalLabel } from "../ErrorLogModal/ErrorLogModalLabelContainer";
import { PaymentBanner } from "../PaymentBanner/PaymentBanner";

import {
    ActionButtons,
    ErrorCountersContainer,
    Footer,
    MainContent,
    PaymentBannerContainer,
    PrintErrorlogContainer,
    ResultContainer,
    WidePanelLogRoot,
} from "./WidePanelLog.styles";

export interface WidePanelLogProps {
    useExtendedErrorLog: boolean;
    allowSend: boolean;
    errorLog: ErrorLog;
    modelChanged?: boolean;
    hasNotLoadedAttachments: boolean;
    isDisabledCloseAndSaveButton?: boolean;
    canCheckByPopd?: boolean;
    onCheckErrors?: () => void;
    additionalHeaderOffset?: number;
}

interface ErrorListWrapperProps {
    hasInternalErrors: boolean;
}
const ErrorsListWrapper: React.FC<ErrorListWrapperProps> = ({ children, hasInternalErrors }) =>
    hasInternalErrors ? <ScrollContainer>{children}</ScrollContainer> : <div>{children}</div>;

export function WidePanelLog(props: WidePanelLogProps): JSX.Element {
    const { errorLog, hasNotLoadedAttachments, isDisabledCloseAndSaveButton, onCheckErrors, allowSend, modelChanged } =
        props;

    const [isFixed, setIsFixed] = React.useState(false);

    function renderButtons(): JSX.Element {
        if (allowSend) {
            const formNotReady = hasNotLoadedAttachments || modelChanged;
            const isSendDisabled = checkSendDisabled(errorLog) || formNotReady;

            if (errorLog.marketplaceContext?.customScenarios?.bushTestDrive) {
                return (
                    <Gapped>
                        <ActionButtonContainer
                            onCheckErrors={onCheckErrors}
                            place="embeddedLog"
                            type="check"
                            use="default"
                            width={ActionPanelButtonWidth.middleSize3}
                        />
                        <ActionButtonContainer
                            place="embeddedLog"
                            type="close"
                            use="default"
                            width={ActionPanelButtonWidth.middleSize3}
                        />
                    </Gapped>
                );
            }

            return (
                <Gapped>
                    <SendActionButtonContainer
                        disabled={isSendDisabled}
                        place="embeddedLog"
                        width={ActionPanelButtonWidth.middleSize2}
                    />
                    <ActionButtonContainer
                        onCheckErrors={onCheckErrors}
                        place="embeddedLog"
                        type="check"
                        use="default"
                        width={ActionPanelButtonWidth.middleSize1}
                    />
                    <ActionButtonContainer
                        place="embeddedLog"
                        type="close"
                        use="default"
                        width={ActionPanelButtonWidth.smallSize}
                    />
                </Gapped>
            );
        }

        return (
            <Gapped>
                {!isDisabledCloseAndSaveButton && (
                    <ActionButtonContainer place="embeddedLog" type="save" width={ActionPanelButtonWidth.largeSize} />
                )}
                <ActionButtonContainer
                    onCheckErrors={onCheckErrors}
                    place="embeddedLog"
                    type="check"
                    use="default"
                    width={ActionPanelButtonWidth.middleSize2}
                />
            </Gapped>
        );
    }

    const hasInternalErrors = React.useMemo(
        () => hasInternalLog(errorLog),
        [errorLog.errorsTotalCount, errorLog.warningsTotalCount, errorLog.requestFailed, errorLog.hasErrors]
    );

    if (errorLog.isChecking) {
        return <></>;
    }

    return (
        <WidePanelLogRoot>
            <ErrorCountersContainer>
                <ErrorLogCounters errorLog={errorLog} useExtendedErrorLog={props.useExtendedErrorLog} />
            </ErrorCountersContainer>
            <MainContent>
                <ErrorsListWrapper hasInternalErrors={hasInternalErrors}>
                    <Sticky side="top">{setIsFixed}</Sticky>
                    {hasInternalErrors && (
                        <ErrorGroupHeaderSticky
                            title={"Отчет"}
                            tid="protocol-main"
                            additionalHeaderOffset={props.additionalHeaderOffset}
                        />
                    )}
                    {props.canCheckByPopd && !errorLog.requestFailed && (
                        <PopdProtocol hasInternalErrors={hasErrors(errorLog)} />
                    )}
                    {hasInternalErrors && (
                        <ErrorsList
                            showJustification={false}
                            useExtendedErrorLog={props.useExtendedErrorLog}
                            errorLog={errorLog}
                            showSuccesses={false}
                            additionalHeaderOffset={props.additionalHeaderOffset}
                        />
                    )}
                </ErrorsListWrapper>
                {!hasInternalErrors && (
                    <Center>
                        <ResultContainer>
                            <CheckingStatus
                                hasNotLoadedAttachments={hasNotLoadedAttachments}
                                modelChanged={modelChanged}
                                requestFailed={Boolean(errorLog.requestFailed)}
                                errorLog={errorLog}
                                useExtendedErrorLog={props.useExtendedErrorLog}
                            />
                        </ResultContainer>
                    </Center>
                )}
            </MainContent>
            <Footer isFixed={isFixed}>
                {hasInternalErrors && (
                    <PrintErrorlogContainer>
                        {props.useExtendedErrorLog ? (
                            <ErrorLogModalLabel
                                openedFrom={OpenExtendedErrorLogFromPlace.fromActionPanelDownButton}
                                errorLog={errorLog}
                                type={"button"}
                                hasInternalErrors={hasInternalErrors}
                            />
                        ) : (
                            <PrintErrorLog />
                        )}
                    </PrintErrorlogContainer>
                )}
                {!hasErrors(errorLog) && errorLog.marketplaceContext?.customScenarios?.bushTestDrive && (
                    <PaymentBannerContainer>
                        <PaymentBanner
                            urlUsn={errorLog.marketplaceContext.customScenarios.bushTestDrive.urlUsn}
                            urlOsno={errorLog.marketplaceContext.customScenarios.bushTestDrive.urlOsno}
                        />
                    </PaymentBannerContainer>
                )}
                <ActionButtons errors={hasInternalErrors}>{renderButtons()}</ActionButtons>
                <FormSaveStatus />
            </Footer>
        </WidePanelLogRoot>
    );
}

function hasInternalLog(errorLog: ErrorLog): boolean {
    const hasErrorsOrWarnings = errorLog.hasErrors || hasErrors(errorLog) || errorLog.warningsTotalCount > 0;

    const errorLogRequestFailed = Boolean(errorLog.requestFailed);

    return hasErrorsOrWarnings && !errorLogRequestFailed;
}
